// frontend/src/contexts/AuthContext.js (Updated for new contract system)
import React, { createContext, useContext, useState, useEffect } from "react";
import authService from "../services/authService";
import api from "../config/api";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCoach, setIsCoach] = useState(false);

  useEffect(() => {
    initializeAuth();
  }, []);

  const fetchCoachStatus = async () => {
    try {
      const response = await api.get("/auth/coach-status");
      setIsCoach(!!response.data?.isCoach);
    } catch (error) {
      setIsCoach(false);
    }
  };

  const initializeAuth = async () => {
    try {
      if (authService.isAuthenticated()) {
        const storedUser = authService.getStoredUser();
        if (storedUser) {
          // Verify token is still valid
          const result = await authService.verifyToken();
          if (result.success) {
            setUser(result.user);
            setIsAuthenticated(true);
            await fetchCoachStatus();
          } else {
            // Token invalid, clear storage
            authService.logout();
          }
        }
      }
    } catch (error) {
      console.error("Auth initialization error:", error);
      authService.logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      const result = await authService.login(username, password);

      if (result.success) {
        setUser(result.user);
        setIsAuthenticated(true);
        await fetchCoachStatus();
        return { success: true };
      }

      return { success: false, error: result.error };
    } catch (error) {
      return { success: false, error: "Login failed" };
    }
  };

  const logout = () => {
    authService.logout();
    setUser(null);
    setIsAuthenticated(false);
    setIsCoach(false);
  };

  const hasAccess = (requiredContract) => {
    // Free content - always accessible
    if (!requiredContract || requiredContract === "Free") {
      return true;
    }

    // Registered content - requires authentication
    if (requiredContract === "Registered") {
      return isAuthenticated;
    }

    // Paid content - requires authentication and burnairMap contract
    if (!user || !user.contracts) {
      return false;
    }

    // Filter for burnairMap contracts only
    const burnairMapContracts = user.contracts.filter(
      (c) => c.type === "burnairMap"
    );

    if (burnairMapContracts.length === 0) {
      return false;
    }

    // Check if any burnairMap contract is active and has sufficient level
    const now = Math.floor(Date.now() / 1000); // Current timestamp in seconds
    const contractHierarchy = { basic: 1, premium: 2, pro: 3 };
    const requiredLevel =
      contractHierarchy[requiredContract.toLowerCase()] || 0;

    const activeContracts = burnairMapContracts.filter((contract) => {
      const isActive = contract.expiration > now;
      const hasLevel =
        contractHierarchy[contract.level_label?.toLowerCase()] >= requiredLevel;

      return isActive && hasLevel;
    });

    return activeContracts.length > 0;
  };

  const value = {
    user,
    isAuthenticated,
    loading,
    login,
    logout,
    hasAccess,
    isCoach,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
